<template>
	<div>
		<NoteBlock v-if="readOnlyMode" variant="notice" class="mb-4 text-center font-medium">
			{{ t('read_only_availability_notice') }}
		</NoteBlock>

		<div class="pb-6 mb-6 border-b border-b-gray-200">
			<h1>{{ planTitle }}</h1>
			<p v-if="$flowstore.checkout.meta.text.reservation.description !== ''">
				{{ $flowstore.checkout.meta.text.reservation.description }}
			</p>
		</div>

		<div v-if="flowStore.checkoutTypes.length && !readOnlyMode" class="grid grid-cols-2 gap-4 mb-4">
			<label class="flex items-center" for="what_would_you_like_to_book">{{ t('what_would_you_like_to_book') }}</label>
			<SelectField
				id="what_would_you_like_to_book"
				v-model="arrangement"
				variant="outlined"
				:optional="false"
				:options="flowStore.checkoutTypes.map(entry => ({
        value: entry.type,
        label: entry.name,
      }))"
			/>
		</div>

		<div :class="['grid 2xs:grid-cols-2 gap-4 mb-4', {'pb-6 mb-6 border-b border-b-gray-200': !flowStore.isHotelFlow}]">
			<label class="flex items-center">{{ t('persons_count') }} </label>
			<IncrementNumberField
				v-if="!readOnlyMode"
				:model-value="$flowstore.checkout.data.persons_count"
				:min="$flowstore.checkout.meta.person_min"
				:max="$flowstore.checkout.meta.person_max"
				label="number_of_persons"
				data-cy="number_of_persons"
				type-label="persons_count"
				@update:model-value="flowStore.setPersonsCount($event)"
			/>
			<div v-else class="flex items-center justify-center rounded-full border border-qwr-haze-100 h-9 px-4">1</div>
		</div>

		<div
			v-if="flowStore.isHotelFlow && !readOnlyMode"
			class="grid 2xs:grid-cols-2 gap-4 pb-6 mb-6 border-b border-b-gray-200"
		>
			<label class="flex items-center gap-2">
				{{ t('rooms_count') }}
				<ToolTip align="left">
					<div v-html="t('are_you_coming_together_but_still_want_to_enjoy_your_own_room')" />
				</ToolTip>
			</label>
			<IncrementNumberField
				:model-value="$flowstore.checkout.data.amount_of_rooms"
				:min="$flowstore.checkout.meta.min_rooms_for_persons ?? 1"
				:max="$flowstore.checkout.data.persons_count"
				label="amount_of_rooms"
				data-cy="amount_of_rooms"
				type-label="rooms_count"
				@update:model-value="flowStore.setRoomsCount($event)"
			/>
		</div>

		<Transition name="fade">
			<div v-if="flowStore.checkout.calendar_notifications_above">
				<NoteBlock
					v-for="(notice, index) in flowStore.checkout.calendar_notifications_above"
					:key="`not_cna_${index}`"
					class="my-6"
					variant="notice"
				>
					<HtmlContent :html="notice" />
				</NoteBlock>
			</div>
		</Transition>

		<h2 class="text-lg font-sans font-normal mb-7 flex justify-between items-center gap-6">
			{{
        t(flowStore.isHotelFlow ? 'choose_your_arrival_and_departure_day_in_the_calendar' : 'when_are_you_coming_to_relax')
			}}

			<span v-if="flowStore.isHotelFlow" class="flex justify-end gap-2">
				<span
					:class="{
          'inline-flex items-center justify-center h-6 border rounded-full px-2 text-xs min-w-[3.55rem]': true,
          'bg-primary border-primary text-primary-letter': startDate,
          'bg-transparent border-black': !startDate,
        }"
					>{{ startDate ? format(startDate, 'd LLL', {locale}) : t('arrival') }}</span
				>
				<span
					:class="{
          'inline-flex items-center justify-center h-6 border rounded-full px-2 text-xs min-w-[3.55rem]': true,
          'bg-primary border-primary text-primary-letter': endDate && endDateIsAfterStart,
          'bg-transparent border-black': !endDate || !endDateIsAfterStart,
        }"
					>{{ endDate && endDateIsAfterStart ? format(endDate, 'd LLL', {locale}) : t('departure') }}</span
				>
			</span>
		</h2>

		<div
			v-if="(showSwimwearDayInfo || flowStore.entreeProducts.length > 1) && !readOnlyMode"
			class="flex flex-wrap gap-2 mb-8 justify-between"
		>
			<div class="flex flex-wrap gap-2">
				<ActionButton
					v-for="entreeProduct in flowStore.entreeProducts"
					:key="entreeProduct.key"
					variant="light"
					:active="flowStore.checkout.data.product_id === entreeProduct.product_id"
					@click="changeMainProduct(entreeProduct.product_id)"
				>
					{{ t(entreeProduct.key) }}
				</ActionButton>
			</div>
			<ToggleField v-if="showSwimwearDayInfo" v-model="highlightSwimwearDays" slot-position="before" class="max-lg:hidden">
				{{ t('filter_swimwear_days') }}
			</ToggleField>
		</div>

		<Transition name="fade">
			<NoteBlock v-if="$validationstore.errors.departure_date" variant="error" class="my-6 error-scroll-anchor">
				{{ $validationstore.errors.departure_date[0] }}
			</NoteBlock>
		</Transition>

		<StandardCard class="relative">
			<LoadingOverlay v-if="$loadingstore.changing_main_product" />
			<DatePicker
				:start-date="startDate"
				:end-date="endDate"
				:date-options="calendarData"
				:language="config.language"
				selected-color="hsl(var(--primary), var(--primary-lightness))"
				selected-text-color="#ffffff"
				hover-text-color="#ffffff"
				hover-color="hsla(var(--primary), var(--primary-lightness), 0.5)"
				:range="flowStore.isHotelFlow"
				:static-range="flowStore.checkout.meta.exact_number_of_nights ?? undefined"
				@month:change="({event, focus, dates}) => loadCalendarData(focus.getFullYear(), focus.getMonth() + 1)"
				@day:change="changeCalendarDays"
			>
				<template #day="day">
					<div class="font-semibold text-base leading-5">
						{{ day.raw.getDate() }}
					</div>
					<!--
            Notification marker. No transform for the offset (a transform creates a stacking context that
            traps the tooltip popover behind later cells) and the z-index sits on the marker itself, so it
            clears the badge (z-20) without capping the popover.
          -->
					<div v-if="day.options.notifications?.length" class="absolute top-0.5 right-0.5 sm:top-1.5 sm:right-1.5">
						<ToolTip align="left">
							<template #toggle>
								<span class="relative z-30 flex flex-col gap-0.5">
									<span
										v-for="iconClass in notificationIconClasses(day)"
										:key="iconClass"
										class="flex size-2.75 sm:size-3.5 items-center justify-center rounded-full bg-white ring-1 ring-white"
									>
										<InfoCircle class="size-2.75 sm:size-3.5" :class="iconClass" />
									</span>
								</span>
							</template>
							<HtmlContent
								v-for="(notification, i) in day.options.notifications"
								:key="`notification_${i}`"
								:html="notification"
								:class="{ 'mt-4 pt-4 border-t border-qwr-haze-100': i > 0 }"
							/>
						</ToolTip>
					</div>
					<div
						v-if="dayBadgeLabel(day)"
						class="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 z-20 pointer-events-none whitespace-nowrap rounded-lg bg-ink text-white px-1.75 py-1 text-3xs font-medium leading-trim"
					>
						{{ dayBadgeLabel(day) }}
					</div>
					<!-- No price on the departure day: the last night of a hotel range is never billed -->
					<PriceText
						v-if="!hasExtraNightPricing && ((!day.options.inactive && day.options.price && !day.options.lastDayInRange) || flowStore.checkout.meta.overwrite_fixed_price_for_exact_nights)"
						:value="dayPrice(day)"
						class="text-micro"
						:class="dayPriceClass(day)"
					/>
					<div
						v-if="highlightSwimwearDays && day.options.status === 'swimwear'"
						class="absolute inset-0 rounded-lg ring-2 ring-inset ring-swimwear pointer-events-none"
					></div>
					<svg
						v-if="highlightSwimwearDays && day.options.status !== 'swimwear'"
						class="absolute inset-0 size-full pointer-events-none text-qwr-haze-300 opacity-50"
						preserveAspectRatio="none"
					>
						<line x1="20%" y1="20%" x2="80%" y2="80%" stroke="currentColor" stroke-width="1" />
						<line x1="80%" y1="20%" x2="20%" y2="80%" stroke="currentColor" stroke-width="1" />
					</svg>
				</template>
			</DatePicker>
			<div class="flex flex-col gap-4 px-5 pb-5 text-micro text-ink-muted">
				<div class="flex flex-wrap items-center gap-x-5 gap-y-2">
					<ToolTip v-if="showSwimwearDayInfo" align="left" :aria-label="t('swimwear_day')">
						<template #toggle>
							<div class="flex gap-2 items-center"><InfoCircle class="size-3.5 text-swimwear" /> {{ t('swimwear_day') }}</div>
						</template>
						{{ t('swimwear_day_tooltip_sentence') }}
					</ToolTip>
					<div v-if="showSpecialDayInfo" class="flex gap-2 items-center">
						<InfoCircle class="size-3.5 text-gold-500" /> {{ t('special_day') }}
					</div>
					<div v-if="!hasExtraNightPricing" class="flex gap-2 items-center">
						<span class="size-3.5 rounded-sm bg-primary"></span>
						{{ t('arrival_or_departure_day') }}
					</div>
				</div>
				<div v-if="!hasExtraNightPricing" class="text-right">
					{{ t(flowStore.isHotelFlow ? 'prices_are_in_euro_per_night' : 'prices_are_in_euro') }}
				</div>
			</div>

			<ExtraNightPricingSummary
				v-if="hasExtraNightPricing"
				ref="extraNightSummary"
				class="lg:hidden border-t border-qwr-haze-100 px-5 py-4"
			/>
		</StandardCard>

		<Transition name="fade">
			<div v-if="flowStore.checkout.calendar_notifications">
				<NoteBlock v-for="(notice, i) in flowStore.checkout.calendar_notifications" :key="`nb_cal_not_${i}`" class="my-6">
					<HtmlContent :html="notice" />
				</NoteBlock>
			</div>
		</Transition>

		<Transition name="fade">
			<div v-if="flowStore.checkout.notices">
				<NoteBlock
					v-for="(notice, i) in flowStore.checkout.notices"
					:key="`nb_check_not_${i}`"
					variant="error"
					class="my-6"
				>
					{{ notice[0] }}
				</NoteBlock>
			</div>
		</Transition>

		<ArrivalAndDepartureTime />

		<Transition name="fade">
			<NoteBlock v-if="generalErrors?.length > 0" variant="error" class="my-6 error-scroll-anchor">
				{{ generalErrors[0] }}
			</NoteBlock>
		</Transition>
	</div>
</template>

<script lang="ts" setup>
import config, {dateLocale as locale} from '@/config';
import {computed, nextTick, onMounted, ref, watch} from 'vue';
import {format, isAfter} from 'date-fns';
import {useFlowStore} from '@/stores/useFlowStore';
import {useRoute} from 'vue-router';
import api from '@/api';
import StandardCard from '@/components/StandardCard.vue';
import DatePicker, {type DateOptions, type ViewerDate} from '@/components/datepicker/DatePicker.vue';
import NoteBlock from '@/components/NoteBlock.vue';
import IncrementNumberField from '@/components/fields/IncrementNumberField.vue';
import PriceText from '@/components/PriceText.vue';
import LoadingOverlay from '@/components/LoadingOverlay.vue';
import {type CalendarResource} from '@/api/resources';
import ToolTip from '@/components/ToolTip.vue';
import InfoCircle from '@thermenresorts/icons/svg/info-filled.svg?component';
import {useValidationStore} from '@/stores/useValidationStore';
import SelectField from '@/components/fields/SelectField.vue';
import HtmlContent from '@/components/HtmlContent.vue';
import bridge from '@thermenresorts/bridge';
import ArrivalAndDepartureTime from '@/components/ArrivalAndDepartureTime.vue';
import ActionButton from "@/components/ActionButton.vue";
import ExtraNightPricingSummary from '@/components/receipt/ExtraNightPricingSummary.vue';
import {useExtraNightPricing} from '@/composables/useExtraNightPricing';
import { useI18n } from 'vue-i18n';
import ToggleField from '@/components/fields/ToggleField.vue';

const { t } = useI18n();
const flowStore = useFlowStore();
const validationStore = useValidationStore();
const route = useRoute();

// Dynamic first-screen title so guests recognise the product they picked, e.g. "Plan jouw Entree".
const planTitle = computed(() => {
  const product = flowStore.checkout.meta.product.name;
  return product ? t('plan_your', { product }) : flowStore.checkout.meta.text.reservation.title;
});

const calendarData = ref<DateOptions>({});
const arrangement = ref('sauna-entree');
const showSwimwearDayInfo = ref(false);
const highlightSwimwearDays = ref(false);
const showSpecialDayInfo = ref(false);

const {isEnabled: hasExtraNightPricing} = useExtraNightPricing();

function dayBadgeLabel(day: ViewerDate): string | null {
  if (!hasExtraNightPricing.value) return null;

  const {isPendingArrival, firstDayInRange, lastDayInRange, nightsInRange} = day.options;

  if (isPendingArrival || (firstDayInRange && nightsInRange)) return t('arrival_badge');
  if (lastDayInRange) return t('departure_badge');

  return null;
}

function notificationIconClasses({options}: ViewerDate): string[] {
  const classes: string[] = [];

  if (options.status === 'swimwear') classes.push('text-swimwear');
  if (options.isSpecialDay) classes.push('text-gold-500');

  return classes.length ? classes : ['text-ink'];
}

function dayPriceClass(day: ViewerDate): string {
  const {isSelected, isHovered, status} = day.options;

  if (isSelected || isHovered) return 'text-white/85';
  if (status === 'swimwear') return 'text-swimwear';

  return 'text-ink-muted';
}

function dayPrice(day: ViewerDate): string | null | undefined {
  const fixedPrice = flowStore.checkout.meta.overwrite_fixed_price_for_exact_nights;

  if (fixedPrice && flowStore.checkout.data.arrival.date === day.options.date) return fixedPrice;

  return day.options.price;
}

watch(arrangement, (value) => {
  if (readOnlyMode.value) return;

  if (value !== 'sauna-entree') {
    const checkoutType = flowStore.checkoutTypes.find((entry) => entry.type === value);
    if (checkoutType) {
      bridge.sendMessage('reservation-flow', 'redirect', {tag: 'FLOW_CHANGE', url: checkoutType.redirect_url});
      bridge.sendMessage('reservation-flow', 'reservation-flow.close');

      if (!bridge.isIntergrated) window.location.href = checkoutType.redirect_url;
    }
  }
});

watch(() => validationStore.errors, async () => {
  await nextTick();

  const scrollAnchors = Array.from(document.querySelectorAll<HTMLElement>('.error-scroll-anchor'))
    .filter(el => !('_isLeaving' in el)); // Filter out elements that are animated out. NOTE: Hacky internal VueJS thing

  if (scrollAnchors.length > 0) scrollAnchors[0].scrollIntoView({behavior: 'smooth', block: 'center'});
});

const startDate = computed(() => flowStore.startDate);
const endDate = computed(() => flowStore.endDate);
const readOnlyMode = computed(() => route.query.ro === 'true');

const endDateIsAfterStart = computed(() => {
  if (startDate.value && endDate.value && startDate.value) {
    return isAfter(endDate.value, startDate.value);
  }
  return false;
});

const extraNightSummary = ref<InstanceType<typeof ExtraNightPricingSummary>>();

async function changeCalendarDays(arrivalDate: string, departureDate: string | null) {
  await flowStore.setCheckoutDate(arrivalDate, departureDate);

  if (!departureDate) return;

  await nextTick();
  extraNightSummary.value?.scrollIntoView();
}

function mergeCalendarData(data: CalendarResource) {
  calendarData.value = {
    ...calendarData.value,
    ...Object.fromEntries(Object.entries(data.dates)
      .map(([key, options]) => {
        return [key, {
          inactive: !options.available,
          price: options.price,
          date: options.date,
          status: options.status,
          isSpecialDay: options.is_special_day,
          classes: options.status === 'swimwear' ? 'swimwear-day' : '',
          notifications: options.notifications,
          extraNightPrice: options.extra_night_price,
        }];
      })),
  };
}

async function loadCalendarData(year: number, month: number) {
  // Don't await this, we want to load data front the next month in the background.
  prefetchCalendarData(year, month);

  const data = await api.calendar.get(flowStore.checkout.data.checkout_id, year, month);
  if (data) mergeCalendarData(data);

  showSwimwearDayInfo.value = Object.entries(data.dates)
    .some(([, options]) => options.status === 'swimwear');
  showSpecialDayInfo.value = Object.values(data.dates)
    .some((options) => options.is_special_day);
}

async function prefetchCalendarData(year: number, month: number) {
  const nextMonth = (month + 1 > 12 ? 1 : month + 1);
  const data = await api.calendar.get(flowStore.checkout.data.checkout_id, year, nextMonth, 'prefetch');
  if (data) mergeCalendarData(data);
}

onMounted(() => {
  loadCalendarData(startDate.value.getFullYear(), startDate.value.getMonth() + 1);
});

// Change the main flow product and reload calendar data.
const changeMainProduct = async (product_id: number) => {
  await flowStore.changeMainProduct(product_id);
  await loadCalendarData(startDate.value.getFullYear(), startDate.value.getMonth() + 1);
};

const generalErrors = computed(
  () =>  validationStore.errorsExceptFlat([
    'arrival_time',
    'arrival_date_and_time',
    'departure_date',
    'departure_time',
    'product_choices'
  ])
);
</script>
